import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { GameMode, AOTCharacter, GuessResult, Language, PageView } from './types';
import { getDailyCharacter, getDayNumber, getYesterdayCharacter, AOT_CHARACTERS, ARC_ORDER, HEIGHT_ORDER } from './constants';
import Autocomplete from './components/Autocomplete';
import ResultRow from './components/ResultRow';
import { HelpModal, StatsModal, SettingsModal } from './components/Modals';
import NavBar from './components/NavBar';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import LegalPages from './components/LegalPages';
import { CONFIG } from './config';
import { TRANSLATIONS } from './translations';
import { Lock, Copy, Check, Sword, MessageSquareQuote, Image } from 'lucide-react';

// Lazy load content pages for better initial load time
const CharacterGallery = lazy(() => import('./components/CharacterGallery'));
const GuidePage = lazy(() => import('./components/GuidePage'));
const TriviaPage = lazy(() => import('./components/TriviaPage'));

const PAGE_TO_PATH: Record<PageView, string> = {
    game: '/',
    characters: '/characters',
    guide: '/guide',
    trivia: '/trivia',
    privacy: '/privacy',
    about: '/about',
    contact: '/contact'
};

const PATH_TO_PAGE: Record<string, PageView> = {
    '/': 'game',
    '/characters': 'characters',
    '/guide': 'guide',
    '/trivia': 'trivia',
    '/privacy': 'privacy',
    '/about': 'about',
    '/contact': 'contact'
};

function App() {
    // Helper to detect browser language
    const getBrowserLanguage = (): Language => {
        const browserLang = navigator.language?.slice(0, 2).toLowerCase();
        if (browserLang === 'tr') return 'tr';
        if (browserLang === 'es') return 'es';
        return 'en'; // Default to English for all other languages
    };

    // Helper to get params from URL
    const getUrlParams = () => {
        const params = new URLSearchParams(window.location.search);
        const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
        const pageFromPath = PATH_TO_PAGE[normalizedPath];
        const rawMode = params.get('mode');
        const mode = rawMode && rawMode.length > 0
            ? `${rawMode.charAt(0).toUpperCase()}${rawMode.slice(1).toLowerCase()}`
            : null;
        const langParam = params.get('lang');

        const page = pageFromPath || (params.get('page') as PageView | null);

        return {
            mode: page && page !== 'game' ? null : mode as GameMode | null,
            page,
            lang: (langParam && ['en', 'tr', 'es'].includes(langParam)) ? langParam as Language : null
        };
    };

    // State
    const [language, setLanguage] = useState<Language>(() => {
        const { lang } = getUrlParams();
        if (lang) return lang;
        const saved = localStorage.getItem(CONFIG.STORAGE_KEYS.LANGUAGE);
        if (saved && ['en', 'tr', 'es'].includes(saved)) {
            return saved as Language;
        }
        return getBrowserLanguage();
    });

    // Initialize state from URL or defaults
    const [mode, setMode] = useState<GameMode | null>(() => {
        const { mode } = getUrlParams();
        // Validate mode
        if (mode && Object.values(GameMode).includes(mode)) {
            return mode;
        }
        return null;
    });

    const [guesses, setGuesses] = useState<GuessResult[]>([]);
    const [isWon, setIsWon] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [showStats, setShowStats] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    const [pageView, setPageView] = useState<PageView>(() => {
        const { page } = getUrlParams();
        // Validate page
        if (page && ['game', 'characters', 'guide', 'trivia', 'privacy', 'about', 'contact'].includes(page)) {
            return page;
        }
        return 'game';
    });
    const [isStorageLoaded, setIsStorageLoaded] = useState(false);

    const t = TRANSLATIONS[language];
    const currentDayNumber = getDayNumber();

    // Get daily character based on mode
    const dailyCharacter = useMemo(() => {
        if (!mode) return null;
        return getDailyCharacter(mode);
    }, [mode, currentDayNumber]);

    // Yesterday's data - use centralized function from constants
    const yesterdayData = useMemo(() => {
        if (!mode) return null;
        const yesterdayNumber = currentDayNumber - 1;
        if (yesterdayNumber < 0) return null;

        const character = getYesterdayCharacter(mode);
        if (!character) return null;

        return {
            character,
            number: yesterdayNumber
        };
    }, [mode, currentDayNumber]);

    // Update URL when state changes
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        // Handle Game Mode URL
        if (mode) {
            params.set('mode', mode.toLowerCase());
            params.delete('page'); // Clear page if in game mode
        } else {
            params.delete('mode');
        }

        // Handle Page View URL
        if (pageView !== 'game') {
            params.delete('page');
            params.delete('mode'); // Clear mode if in content page
        } else if (!mode) {
            params.delete('page');
        }

        params.set('lang', language);

        const targetPath = mode ? '/' : PAGE_TO_PATH[pageView];
        const newUrl = `${targetPath}${params.toString() ? '?' + params.toString() : ''}`;
        const currentUrl = `${window.location.pathname}${window.location.search}`;
        if (currentUrl !== newUrl) {
            window.history.replaceState({}, '', newUrl);
        }

    }, [mode, pageView, language]);

    // Handle browser back/forward buttons
    useEffect(() => {
        const handlePopState = () => {
            const { mode: urlMode, page: urlPage, lang: urlLang } = getUrlParams();
            const validMode = urlMode && Object.values(GameMode).includes(urlMode) ? urlMode : null;

            // Only update if different from current state
            if (validMode !== mode) {
                setMode(validMode);
                // Reset game state if switching modes via back button
                if (validMode) {
                    setGuesses([]);
                    setIsWon(false);
                    setIsStorageLoaded(false);
                }
            }

            if (urlPage !== pageView) {
                setPageView(urlPage || 'game');
            }

            if (urlLang && urlLang !== language) {
                setLanguage(urlLang);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [mode, pageView, language]);

    // Load saved game state
    useEffect(() => {
        if (!mode) return;

        const storageKey = `${CONFIG.STORAGE_KEYS.GAME_STATE}-${mode}`;
        const savedString = localStorage.getItem(storageKey);

        if (savedString) {
            try {
                const savedState = JSON.parse(savedString);
                if (savedState.dayNumber === currentDayNumber) {
                    setGuesses(savedState.guesses || []);
                    setIsWon(savedState.isWon || false);
                }
            } catch (e) {
                console.error('Error parsing saved state', e);
            }
        }
        setIsStorageLoaded(true);
    }, [mode, currentDayNumber]);

    // Save game state
    useEffect(() => {
        if (!mode || !isStorageLoaded) return;

        const storageKey = `${CONFIG.STORAGE_KEYS.GAME_STATE}-${mode}`;
        const stateToSave = {
            dayNumber: currentDayNumber,
            guesses,
            isWon
        };

        localStorage.setItem(storageKey, JSON.stringify(stateToSave));
    }, [guesses, isWon, mode, isStorageLoaded, currentDayNumber]);

    // Save language preference and update HTML lang attribute
    useEffect(() => {
        localStorage.setItem(CONFIG.STORAGE_KEYS.LANGUAGE, language);
        document.documentElement.lang = language;
    }, [language]);

    useEffect(() => {
        const pageTitles: Record<PageView, string> = {
            game: 'AOTdle - Daily Attack on Titan Guessing Game',
            characters: `${t.characters_title || 'Character Gallery'} | AOTdle`,
            guide: `${t.guide_title || 'How to Play'} | AOTdle`,
            trivia: `${t.trivia_title || 'Attack on Titan Trivia'} | AOTdle`,
            privacy: `${t.privacy_title || 'Privacy Policy'} | AOTdle`,
            about: `${t.about_title || 'About AOTdle'} | AOTdle`,
            contact: `${t.contact_title || 'Contact'} | AOTdle`
        };

        const pageDescriptions: Record<PageView, string> = {
            game: t.footer_description || 'Daily Attack on Titan character guessing game.',
            characters: t.characters_subtitle || 'Explore all Attack on Titan characters.',
            guide: t.guide_subtitle || 'Learn how to play AOTdle.',
            trivia: t.trivia_subtitle || 'Discover Attack on Titan trivia facts.',
            privacy: t.privacy_intro || 'Read our privacy policy.',
            about: t.about_text || 'About AOTdle.',
            contact: t.contact_text || 'Contact AOTdle.'
        };

        document.title = pageTitles[pageView];

        const setMetaContent = (name: string, content: string) => {
            const meta = document.querySelector(`meta[name="${name}"]`);
            if (meta) meta.setAttribute('content', content);
        };

        setMetaContent('description', pageDescriptions[pageView]);
        setMetaContent('title', pageTitles[pageView]);

        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) {
            const canonicalUrl = `${CONFIG.SITE_URL}${mode ? '/' : PAGE_TO_PATH[pageView]}?lang=${language}`;
            canonical.setAttribute('href', canonicalUrl);
        }
    }, [pageView, language, mode, t]);

    // Helper function to translate values
    const translateValue = (type: string, value: string): string => {
        const key = `${type}_${value.replace(/ /g, '_')}`;
        return t[key] || value;
    };

    // Handle mode change
    const handleModeChange = (newMode: GameMode | null) => {
        setMode(newMode);
        setGuesses([]);
        setIsWon(false);
        setIsStorageLoaded(false);
    };

    // Compare two characters and generate result
    const compareCharacters = (guessed: AOTCharacter, target: AOTCharacter): GuessResult => {
        const guessedArcNum = ARC_ORDER[guessed.arc];
        const targetArcNum = ARC_ORDER[target.arc];
        const arcDiff = Math.abs(guessedArcNum - targetArcNum);

        const guessedHeightNum = HEIGHT_ORDER[guessed.height];
        const targetHeightNum = HEIGHT_ORDER[target.height];

        return {
            character: guessed,
            isCorrect: guessed.id === target.id,
            comparisons: {
                gender: guessed.gender === target.gender ? 'correct' : 'wrong',
                arc: guessed.arc === target.arc ? 'correct' : arcDiff === 1 ? 'partial' : 'wrong',
                arcDirection: guessedArcNum < targetArcNum ? 'up' : guessedArcNum > targetArcNum ? 'down' : undefined,
                affiliation: guessed.affiliation === target.affiliation ? 'correct' : 'wrong',
                species: guessed.species === target.species ? 'correct' : 'wrong',
                height: guessed.height === target.height ? 'correct' : Math.abs(guessedHeightNum - targetHeightNum) === 1 ? 'partial' : 'wrong',
                heightDirection: guessedHeightNum < targetHeightNum ? 'up' : guessedHeightNum > targetHeightNum ? 'down' : undefined,
                status: guessed.status === target.status ? 'correct' : 'wrong'
            }
        };
    };

    // Handle guess
    const handleGuess = (guessedCharacter: AOTCharacter) => {
        if (!dailyCharacter || isWon) return;

        // Check if already guessed
        if (guesses.some(g => g.character.id === guessedCharacter.id)) return;

        const result = compareCharacters(guessedCharacter, dailyCharacter);
        setGuesses(prev => [result, ...prev]);

        if (result.isCorrect) {
            setIsWon(true);
            // Update stats
            updateStats(true, guesses.length + 1);
            setTimeout(() => setShowStats(true), 1500);
        }
    };

    // Update statistics
    const updateStats = (won: boolean, _attempts: number) => {
        const savedStats = localStorage.getItem(CONFIG.STORAGE_KEYS.STATS);
        const stats = savedStats ? JSON.parse(savedStats) : {
            played: 0,
            won: 0,
            streak: 0,
            maxStreak: 0,
            lastDay: -1
        };

        const today = getDayNumber();
        if (stats.lastDay === today) return; // Already recorded today

        stats.played++;
        if (won) {
            stats.won++;
            if (stats.lastDay === today - 1) {
                stats.streak++;
            } else {
                stats.streak = 1;
            }
            stats.maxStreak = Math.max(stats.maxStreak, stats.streak);
        } else {
            stats.streak = 0;
        }
        stats.lastDay = today;

        localStorage.setItem(CONFIG.STORAGE_KEYS.STATS, JSON.stringify(stats));
    };

    // Victory Component
    const VictorySection = () => {
        const [timeLeft, setTimeLeft] = useState('');
        const [isCopied, setIsCopied] = useState(false);

        useEffect(() => {
            const tick = () => {
                const now = new Date();
                const tomorrow = new Date(now);
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(0, 0, 0, 0);

                const diff = tomorrow.getTime() - now.getTime();
                const hours = Math.floor(diff / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diff % (1000 * 60)) / 1000);

                setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
            };

            tick();
            const interval = setInterval(tick, 1000);
            return () => clearInterval(interval);
        }, []);

        const generateShareText = () => {
            const getStatusEmoji = (status: string) => {
                if (status === 'correct') return '🟩';
                if (status === 'partial') return '🟨';
                return '⬛';
            };

            let text = `⚔️ AOTdle #${getDayNumber()} - ${mode}\n`;
            text += `🎯 ${guesses.length} ${guesses.length === 1 ? t.guess_singular : t.guess_plural}\n\n`;

            guesses.slice().reverse().forEach(g => {
                text += `${getStatusEmoji(g.comparisons.gender)}`;
                text += `${getStatusEmoji(g.comparisons.arc)}`;
                text += `${getStatusEmoji(g.comparisons.affiliation)}`;
                text += `${getStatusEmoji(g.comparisons.species)}`;
                text += `${getStatusEmoji(g.comparisons.height)}`;
                text += `${getStatusEmoji(g.comparisons.status)}\n`;
            });

            text += `\n${CONFIG.SITE_URL}`;
            return text;
        };

        const handleCopy = async () => {
            try {
                await navigator.clipboard.writeText(generateShareText());
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 2000);
            } catch (err) {
                console.error('Failed to copy:', err);
            }
        };

        const shareText = encodeURIComponent(generateShareText());

        return (
            <div className="w-full max-w-xl bg-gradient-to-br from-green-900/90 to-stone-900/95 border-2 border-[#4a7c59]/50 p-6 md:p-8 mb-8 text-center shadow-[0_0_50px_rgba(74,124,89,0.2)] animate-scale-in backdrop-blur-md rounded-lg relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#4a7c59] to-transparent opacity-50"></div>

                <h2 className="text-3xl md:text-4xl font-bold mb-2 text-[#6b9b7a] title-glow">{t.victory_heading}</h2>
                <div className="w-16 h-1 bg-[#4a7c59]/50 mx-auto mb-6"></div>

                <div className="flex flex-col md:flex-row items-center gap-6 mb-8">
                    <div className="w-32 h-32 md:w-40 md:h-40 border-4 border-[#4a7c59]/30 shadow-2xl relative group rounded-lg overflow-hidden">
                        <img
                            src={dailyCharacter?.image}
                            alt={dailyCharacter?.name}
                            className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700"
                            loading="lazy"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                    </div>
                    <div className="flex flex-col text-left">
                        <span className="text-stone-400 text-xs uppercase tracking-widest mb-1">{t.it_was}</span>
                        <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{dailyCharacter?.name}</h3>
                        <p className="text-stone-300 italic">"{dailyCharacter?.quotes[language]}"</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-white/10 pt-6">
                    <div className="flex flex-col items-center justify-center p-3 bg-black/20 rounded">
                        <span className="text-stone-400 text-[10px] uppercase tracking-widest mb-1">{t.next_character}</span>
                        <span className="text-2xl font-mono font-bold text-[#6b9b7a]">{timeLeft}</span>
                    </div>

                    <button
                        onClick={handleCopy}
                        className={`flex items-center justify-center gap-2 w-full p-3 font-bold uppercase tracking-wide text-sm transition-all duration-300 border rounded ${isCopied ? 'bg-green-800 border-green-500 text-white' : 'bg-[#4a7c59] hover:bg-[#2d5a3c] border-[#4a7c59] text-white'}`}
                    >
                        {isCopied ? <Check size={18} /> : <Copy size={18} />}
                        {isCopied ? t.copied : t.share_results}
                    </button>
                </div>

                <div className="flex justify-center gap-4 mt-4">
                    <a
                        href={`https://twitter.com/intent/tweet?text=${shareText}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-stone-800 hover:bg-stone-700 text-white rounded-full transition-colors border border-stone-600"
                        aria-label={t.share_twitter}
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                    </a>
                    <a
                        href={`https://wa.me/?text=${shareText}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-stone-800 hover:bg-stone-700 text-white rounded-full transition-colors border border-stone-600"
                        aria-label={t.share_whatsapp}
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                    </a>
                </div>
            </div>
        );
    };

    // Landing Screen Component (Modern Design)
    const LandingScreen = () => (
        <div className="w-full max-w-5xl flex flex-col items-center animate-fade-in mt-8 md:mt-16 px-4">
            <div className="text-center mb-12">
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-3 tracking-tight">
                    <span className="bg-gradient-to-r from-[#6b9b7a] via-[#4a7c59] to-[#6b9b7a] bg-clip-text text-transparent">{t.choose_challenge}</span>
                </h2>
                <p className="text-stone-400 text-sm md:text-base max-w-md mx-auto">{t.landing_subtitle || "Test your Attack on Titan knowledge"}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-4xl">
                {/* Classic Mode */}
                <a
                    href="?mode=classic"
                    onClick={(e) => {
                        e.preventDefault();
                        handleModeChange(GameMode.CLASSIC);
                    }}
                    className="group relative flex flex-col items-center p-8 bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d] border border-stone-700/50 hover:border-[#4a7c59]/70 rounded-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_8px_40px_rgba(74,124,89,0.2)] focus:outline-none focus:ring-2 focus:ring-[#4a7c59]/50 overflow-hidden no-underline"
                    aria-label={`${t.classic}: ${t.classic_desc}`}
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#4a7c59]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="relative z-10 w-20 h-20 rounded-2xl bg-gradient-to-br from-[#4a7c59]/30 to-[#2d5a3c]/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                        <Sword className="w-10 h-10 text-[#6b9b7a] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#6b9b7a] transition-colors">{t.classic}</h3>
                    <p className="text-stone-400 text-sm text-center group-hover:text-stone-300 transition-colors">{t.classic_desc}</p>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#4a7c59] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </a>

                {/* Quote Mode */}
                <a
                    href="?mode=quote"
                    onClick={(e) => {
                        e.preventDefault();
                        handleModeChange(GameMode.QUOTE);
                    }}
                    className="group relative flex flex-col items-center p-8 bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d] border border-stone-700/50 hover:border-[#4a7c59]/70 rounded-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_8px_40px_rgba(74,124,89,0.2)] focus:outline-none focus:ring-2 focus:ring-[#4a7c59]/50 overflow-hidden text-decoration-none"
                    aria-label={`${t.quote}: ${t.quote_desc}`}
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#4a7c59]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="relative z-10 w-20 h-20 rounded-2xl bg-gradient-to-br from-[#4a7c59]/30 to-[#2d5a3c]/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                        <MessageSquareQuote className="w-10 h-10 text-[#6b9b7a] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#6b9b7a] transition-colors">{t.quote}</h3>
                    <p className="text-stone-400 text-sm text-center group-hover:text-stone-300 transition-colors">{t.quote_desc}</p>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#4a7c59] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </a>

                {/* Image Mode */}
                <a
                    href="?mode=image"
                    onClick={(e) => {
                        e.preventDefault();
                        handleModeChange(GameMode.IMAGE);
                    }}
                    className="group relative flex flex-col items-center p-8 bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d] border border-stone-700/50 hover:border-[#4a7c59]/70 rounded-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_8px_40px_rgba(74,124,89,0.2)] focus:outline-none focus:ring-2 focus:ring-[#4a7c59]/50 overflow-hidden text-decoration-none"
                    aria-label={`${t.image}: ${t.image_desc}`}
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#4a7c59]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="relative z-10 w-20 h-20 rounded-2xl bg-gradient-to-br from-[#4a7c59]/30 to-[#2d5a3c]/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                        <Image className="w-10 h-10 text-[#6b9b7a] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#6b9b7a] transition-colors">{t.image}</h3>
                    <p className="text-stone-400 text-sm text-center group-hover:text-stone-300 transition-colors">{t.image_desc}</p>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#4a7c59] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </a>
            </div>

            {/* Quick Stats */}
            <div className="mt-14 flex flex-wrap justify-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-stone-400">
                    <span className="w-2 h-2 rounded-full bg-[#4a7c59]"></span>
                    {t.stat_characters}
                </div>
                <div className="flex items-center gap-2 text-stone-400">
                    <span className="w-2 h-2 rounded-full bg-[#4a7c59]"></span>
                    {t.stat_arcs}
                </div>
                <div className="flex items-center gap-2 text-stone-400">
                    <span className="w-2 h-2 rounded-full bg-[#4a7c59]"></span>
                    {t.stat_daily}
                </div>
            </div>
            {/* SEO & Game Info Section - Added for AdSense Compliance */}
            <div className="mt-20 w-full max-w-4xl text-stone-300 px-4">
                <div className="grid md:grid-cols-2 gap-10">
                    <div>
                        <h3 className="text-xl font-bold text-[#6b9b7a] mb-4">{t.seo_about_title || "About AOTdle"}</h3>
                        <p className="mb-4 text-sm leading-relaxed text-stone-400">
                            {t.seo_about_text_1}
                        </p>
                        <p className="text-sm leading-relaxed text-stone-400">
                            {t.seo_about_text_2}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-[#6b9b7a] mb-4">{t.seo_features_title || "Game Features"}</h3>
                        <ul className="space-y-2 text-sm text-stone-400">
                            <li className="flex items-start gap-2">
                                <span className="text-[#4a7c59] mt-1">•</span>
                                <span>{t.seo_feature_daily}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#4a7c59] mt-1">•</span>
                                <span>{t.seo_feature_hints}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#4a7c59] mt-1">•</span>
                                <span>{t.seo_feature_spoiler}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#4a7c59] mt-1">•</span>
                                <span>{t.seo_feature_langs}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <p className="mt-12 text-xs text-stone-600 max-w-md text-center">{t.footer_disclaimer}</p>
        </div>
    );

    // Game Interface
    const GameInterface = () => {
        // Filter already guessed characters
        const availableCharacters = AOT_CHARACTERS.filter(
            c => !guesses.some(g => g.character.id === c.id)
        );

        return (
            <div className="w-full max-w-4xl px-4 mt-6 flex flex-col items-center animate-fade-in">

                {/* Mode Selector Tabs */}
                <div className="w-full max-w-md flex justify-center gap-1 mb-6 bg-stone-900/80 p-1 rounded-xl border border-stone-700/50">
                    <button
                        onClick={() => handleModeChange(GameMode.CLASSIC)}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-300 ${mode === GameMode.CLASSIC
                            ? 'bg-[#4a7c59] text-white shadow-lg'
                            : 'text-stone-400 hover:text-white hover:bg-stone-800'
                            }`}
                        aria-pressed={mode === GameMode.CLASSIC}
                    >
                        <Sword size={16} />
                        <span className="hidden sm:inline">{t.classic}</span>
                    </button>
                    <button
                        onClick={() => handleModeChange(GameMode.QUOTE)}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-300 ${mode === GameMode.QUOTE
                            ? 'bg-[#4a7c59] text-white shadow-lg'
                            : 'text-stone-400 hover:text-white hover:bg-stone-800'
                            }`}
                        aria-pressed={mode === GameMode.QUOTE}
                    >
                        <MessageSquareQuote size={16} />
                        <span className="hidden sm:inline">{t.quote}</span>
                    </button>
                    <button
                        onClick={() => handleModeChange(GameMode.IMAGE)}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-300 ${mode === GameMode.IMAGE
                            ? 'bg-[#4a7c59] text-white shadow-lg'
                            : 'text-stone-400 hover:text-white hover:bg-stone-800'
                            }`}
                        aria-pressed={mode === GameMode.IMAGE}
                    >
                        <Image size={16} />
                        <span className="hidden sm:inline">{t.image}</span>
                    </button>
                </div>

                {/* Quote Mode Display */}
                {mode === GameMode.QUOTE && !isWon && dailyCharacter && (
                    <div className="w-full max-w-xl bg-stone-900/90 backdrop-blur-md border-y-2 border-[#4a7c59]/50 p-8 mb-4 text-center relative shadow-xl">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4a7c59] text-white px-4 py-1 text-xs font-bold uppercase tracking-widest shadow-md rounded">
                            {t.quote}
                        </div>
                        <p className="text-xl md:text-2xl italic text-[#e8e4db] leading-relaxed">
                            "{dailyCharacter.quotes[language]}"
                        </p>
                    </div>
                )}

                {/* Hint Boxes (all modes) */}
                {(mode === GameMode.QUOTE || mode === GameMode.CLASSIC || mode === GameMode.IMAGE) && !isWon && dailyCharacter && (
                    <div className="w-full max-w-xl px-2 grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
                        {/* ARC - 2 Guesses */}
                        <div className={`relative flex flex-col items-center justify-center p-2 sm:p-3 border h-16 sm:h-20 transition-all duration-500 rounded ${guesses.length >= 2 ? 'bg-stone-800/95 border-[#4a7c59] shadow-[0_0_15px_rgba(74,124,89,0.15)]' : 'bg-stone-950/95 border-stone-800'}`}>
                            <span className={`text-[10px] uppercase tracking-[0.2em] font-bold mb-2 transition-colors duration-300 ${guesses.length >= 2 ? 'text-[#6b9b7a]' : 'text-stone-600'}`}>{t.col_arc}</span>
                            {guesses.length >= 2 ? (
                                <span className="text-base text-white animate-scale-in font-bold tracking-wide">{translateValue('arc', dailyCharacter.arc)}</span>
                            ) : (
                                <div className="flex items-center gap-2 text-stone-500 animate-fade-in">
                                    <Lock size={12} strokeWidth={2.5} />
                                    <span className="text-[11px] font-semibold tracking-wide uppercase">{t.unlocks_in || 'Unlocks in'} {2 - guesses.length}</span>
                                </div>
                            )}
                        </div>

                        {/* AFFILIATION - 4 Guesses */}
                        <div className={`relative flex flex-col items-center justify-center p-2 sm:p-3 border h-16 sm:h-20 transition-all duration-500 rounded ${guesses.length >= 4 ? 'bg-stone-800/95 border-[#4a7c59] shadow-[0_0_15px_rgba(74,124,89,0.15)]' : 'bg-stone-950/95 border-stone-800'}`}>
                            <span className={`text-[10px] uppercase tracking-[0.2em] font-bold mb-2 transition-colors duration-300 ${guesses.length >= 4 ? 'text-[#6b9b7a]' : 'text-stone-600'}`}>{t.col_affiliation}</span>
                            {guesses.length >= 4 ? (
                                <span className="text-base text-white animate-scale-in font-bold tracking-wide">{translateValue('affiliation', dailyCharacter.affiliation)}</span>
                            ) : (
                                <div className="flex items-center gap-2 text-stone-500 animate-fade-in">
                                    <Lock size={12} strokeWidth={2.5} />
                                    <span className="text-[11px] font-semibold tracking-wide uppercase">{t.unlocks_in || 'Unlocks in'} {4 - guesses.length}</span>
                                </div>
                            )}
                        </div>

                        {/* SPECIES - 6 Guesses */}
                        <div className={`relative flex flex-col items-center justify-center p-2 sm:p-3 border h-16 sm:h-20 transition-all duration-500 rounded ${guesses.length >= 6 ? 'bg-stone-800/95 border-[#4a7c59] shadow-[0_0_15px_rgba(74,124,89,0.15)]' : 'bg-stone-950/95 border-stone-800'}`}>
                            <span className={`text-[10px] uppercase tracking-[0.2em] font-bold mb-2 transition-colors duration-300 ${guesses.length >= 6 ? 'text-[#6b9b7a]' : 'text-stone-600'}`}>{t.col_species}</span>
                            {guesses.length >= 6 ? (
                                <span className="text-base text-white animate-scale-in font-bold tracking-wide">{translateValue('species', dailyCharacter.species)}</span>
                            ) : (
                                <div className="flex items-center gap-2 text-stone-500 animate-fade-in">
                                    <Lock size={12} strokeWidth={2.5} />
                                    <span className="text-[11px] font-semibold tracking-wide uppercase">{t.unlocks_in || 'Unlocks in'} {6 - guesses.length}</span>
                                </div>
                            )}
                        </div>

                        {/* STATUS - 8 Guesses */}
                        <div className={`relative flex flex-col items-center justify-center p-2 sm:p-3 border h-16 sm:h-20 transition-all duration-500 rounded ${guesses.length >= 8 ? 'bg-stone-800/95 border-[#4a7c59] shadow-[0_0_15px_rgba(74,124,89,0.15)]' : 'bg-stone-950/95 border-stone-800'}`}>
                            <span className={`text-[10px] uppercase tracking-[0.2em] font-bold mb-2 transition-colors duration-300 ${guesses.length >= 8 ? 'text-[#6b9b7a]' : 'text-stone-600'}`}>{t.col_status}</span>
                            {guesses.length >= 8 ? (
                                <span className="text-base text-white animate-scale-in font-bold tracking-wide">{translateValue('status', dailyCharacter.status)}</span>
                            ) : (
                                <div className="flex items-center gap-2 text-stone-500 animate-fade-in">
                                    <Lock size={12} strokeWidth={2.5} />
                                    <span className="text-[11px] font-semibold tracking-wide uppercase">{t.unlocks_in || 'Unlocks in'} {8 - guesses.length}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Image Mode Display */}
                {mode === GameMode.IMAGE && !isWon && dailyCharacter && (
                    <div className="w-full max-w-xs bg-stone-900/90 backdrop-blur-md p-3 mb-8 shadow-2xl transform rotate-1 border border-stone-700 rounded-lg">
                        <div className="overflow-hidden bg-black aspect-square relative border border-white/5 rounded">
                            <img
                                src={dailyCharacter.image}
                                alt="Mystery character"
                                className="w-full h-full object-cover object-top transition-all duration-1000 ease-in-out"
                                style={{
                                    filter: `blur(${Math.max(0, 8 - guesses.length * 1.5)}px) grayscale(${Math.max(0, 100 - guesses.length * 15)}%)`
                                }}
                                loading="lazy"
                                onError={(e) => { (e.target as HTMLImageElement).src = '/images/placeholder.webp'; }}
                            />
                        </div>
                    </div>
                )}

                {/* Search Input */}
                {!isWon && (
                    <Autocomplete
                        characters={availableCharacters}
                        onSelect={handleGuess}
                        placeholder={t.search_placeholder}
                        language={language}
                    />
                )}

                {/* Victory Section */}
                {isWon && dailyCharacter && <VictorySection />}

                {/* Results Grid */}
                {guesses.length > 0 && (
                    <>
                        <div className="w-full max-w-4xl grid grid-cols-7 gap-2 mb-3 text-[10px] md:text-xs font-bold uppercase text-stone-300 tracking-widest text-center border-b border-stone-600/50 pb-2 drop-shadow-sm mt-6" role="row">
                            <div role="columnheader" className="hidden md:block">{t.col_character}</div>
                            <div role="columnheader" className="hidden md:block">{t.col_gender}</div>
                            <div role="columnheader" className="hidden md:block">{t.col_arc}</div>
                            <div role="columnheader" className="hidden md:block">{t.col_affiliation}</div>
                            <div role="columnheader" className="hidden md:block">{t.col_species}</div>
                            <div role="columnheader" className="hidden md:block">{t.col_height}</div>
                            <div role="columnheader" className="hidden md:block">{t.col_status}</div>
                        </div>

                        <div className="w-full flex flex-col gap-2" role="grid" aria-label="Guess Results">
                            {guesses.map((guess, index) => (
                                <ResultRow
                                    key={guess.character.id}
                                    guess={guess}
                                    translateValue={translateValue}
                                    index={index}
                                />
                            ))}
                        </div>
                    </>
                )}

                {/* Yesterday's Character */}
                {yesterdayData && (
                    <div className="mt-8 text-xs md:text-sm text-stone-500 tracking-wide animate-fade-in bg-stone-900/50 px-4 py-2 rounded-full border border-stone-800/50 hover:border-[#4a7c59]/30 transition-colors cursor-default">
                        {t.yesterday_character
                            .replace('{number}', yesterdayData.number.toString())
                            .replace('{name}', yesterdayData.character.name)}
                    </div>
                )}
            </div>
        );
    };

    // Render based on page view
    if (pageView !== 'game') {
        const renderContentPage = () => {
            switch (pageView) {
                case 'characters':
                    return (
                        <Suspense fallback={<div className="flex-grow flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#4a7c59] border-t-transparent rounded-full"></div></div>}>
                            <CharacterGallery language={language} />
                        </Suspense>
                    );
                case 'guide':
                    return (
                        <Suspense fallback={<div className="flex-grow flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#4a7c59] border-t-transparent rounded-full"></div></div>}>
                            <GuidePage language={language} />
                        </Suspense>
                    );
                case 'trivia':
                    return (
                        <Suspense fallback={<div className="flex-grow flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#4a7c59] border-t-transparent rounded-full"></div></div>}>
                            <TriviaPage language={language} />
                        </Suspense>
                    );
                default:
                    return <LegalPages pageView={pageView} language={language} setPageView={setPageView} />;
            }
        };

        return (
            <div className="min-h-screen w-full flex flex-col items-center pb-0 relative">
                <img
                    src="./bg.webp"
                    alt=""
                    className="bg-image"
                    aria-hidden="true"
                />
                <div className="bg-overlay" aria-hidden="true"></div>

                <div className="relative z-10 w-full flex flex-col items-center min-h-screen">
                    <NavBar
                        language={language}
                        setLanguage={setLanguage}
                        setShowHelp={setShowHelp}
                        setShowStats={setShowStats}
                        setShowSettings={setShowSettings}
                        onLogoClick={() => { setPageView('game'); handleModeChange(null); }}
                    />
                    {renderContentPage()}
                    <Footer language={language} setPageView={setPageView} />
                    <CookieConsent language={language} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full flex flex-col items-center pb-0 relative">
            <img
                src="./bg.webp"
                alt=""
                className="bg-image"
                aria-hidden="true"
            />
            <div className="bg-overlay" aria-hidden="true"></div>

            <div className="relative z-10 w-full flex flex-col items-center min-h-screen">
                <NavBar
                    language={language}
                    setLanguage={setLanguage}
                    setShowHelp={setShowHelp}
                    setShowStats={setShowStats}
                    setShowSettings={setShowSettings}
                    onLogoClick={() => handleModeChange(null)}
                />

                <main id="main-content" className="w-full flex justify-center flex-1">
                    {!mode ? <LandingScreen /> : <GameInterface />}
                </main>

                <Footer language={language} setPageView={setPageView} />

                {/* Modals */}
                {showHelp && (
                    <HelpModal
                        onClose={() => setShowHelp(false)}
                        language={language}
                    />
                )}
                {showStats && (
                    <StatsModal
                        onClose={() => setShowStats(false)}
                        language={language}
                    />
                )}
                {showSettings && (
                    <SettingsModal
                        onClose={() => setShowSettings(false)}
                        language={language}
                    />
                )}

                <CookieConsent language={language} />
            </div>
        </div>
    );
}

export default App;
